/**
 * Shared styling for a person chip's organization affiliation (#10859, #11056,
 * #9938).
 *
 * A reviewer reading an interaction or the document-review grid could not tell
 * their own colleagues from the manager on the other side of the call. Most
 * people in an RMS are outside contacts, so internal is the only *marked*
 * state: internal people render gold where a surface carries a designator,
 * everyone else keeps the surface's default person styling. The three wire
 * states are preserved by `personChipAffiliation` — unknown must never be
 * collapsed into external.
 *
 * How the gold is *presented* depends on the surface: surfaces that carry a
 * designator tint their own chip; the redesigned card and inline-link
 * surfaces render people as plain ink and never repaint their content area
 * (#11426, keeper ruling A3).
 */

/** Hex for surfaces that run the color through `getEnumColorClasses`. */
export const INTERNAL_ENTITY_CHIP_HEX = "#B7791F";

export type PersonChipAffiliation = "internal" | "external" | "unknown";

/** Preserve all three wire states instead of collapsing null into false. */
export const personChipAffiliation = (
  isInternal: boolean | null | undefined
): PersonChipAffiliation =>
  isInternal === true
    ? "internal"
    : isInternal === false
      ? "external"
      : "unknown";

// The redesigned collection surfaces carry NO affiliation tooltip
// (#11426, Hunter's ruling): most people are external, so "Outside
// contact." on hover restated the default as if it were news, and the
// internal case already has its visual designator (the gold) on the
// surfaces that carry one.

export interface PersonChipColorStyles {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
}

/** CSS colors for grid chips, kept in step with the Tailwind variants above. */
export const personChipColorStyles = (
  isInternal: boolean | null | undefined
): PersonChipColorStyles => {
  switch (personChipAffiliation(isInternal)) {
    case "internal":
      return {
        backgroundColor: "#FDE68A",
        textColor: "#78350F",
        borderColor: "#F59E0B",
      };
    case "external":
      return {
        backgroundColor: "#D5E8EF",
        textColor: "#1A3D4F",
        borderColor: "#A8CEDE",
      };
    case "unknown":
      return {
        backgroundColor: "#F3F4F6",
        textColor: "#1F2937",
        borderColor: "#D1D5DB",
      };
  }
};
